import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../config/axios';
import Navbar from '../components/Navbar';
import CountReview from '../components/CountReview';
import Reports from '../components/Reports';
import ChatModal from '../components/ChatModal';
import ChatBell from '../components/ChatBell';
import SupportModal from '../components/SupportModal';
import ManagerSitesModal from '../components/ManagerSitesModal';
import MissingItemTicketModal from '../components/MissingItemTicketModal';
import MissingItemTicketsPanel from '../components/MissingItemTicketsPanel';
import { Sun, Moon, ClipboardCheck, BarChart3, ArrowLeftRight, PackageMinus, ChevronDown } from 'lucide-react';
import ManagerTransfers from '../components/ManagerTransfers';
import { managerSiteSubmissionStatus } from '../utils/managerCountStatus';
import './Dashboard.css';

const ManagerDashboard = () => {
  const { user, logout } = useAuth();
  const [counts, setCounts] = useState([]);
  const [managerSites, setManagerSites] = useState([]);
  const [brands, setBrands] = useState([]);
  const [allInventory, setAllInventory] = useState([]);
  const [globalInventoryValueLimit, setGlobalInventoryValueLimit] = useState(null);
  const [globalInventoryValueLimitsByCluster, setGlobalInventoryValueLimitsByCluster] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [emptyMessage, setEmptyMessage] = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem('managerDashboard_activeTab');
    // Legacy sidebar entry moved into Reports tabs
    if (saved === 'full-inventory') return 'reports';
    return saved || 'count-review';
  });
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [chatConversationId, setChatConversationId] = useState(null);
  const [chatOtherPartyDisplayName, setChatOtherPartyDisplayName] = useState('');
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatRefreshTrigger, setChatRefreshTrigger] = useState(0);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showSitesModal, setShowSitesModal] = useState(false);
  const [sitesModalType, setSitesModalType] = useState(null);
  const [managerReminderCooldownUntil, setManagerReminderCooldownUntil] = useState(null);
  const [reminderSending, setReminderSending] = useState(false);
  const [reminderMessage, setReminderMessage] = useState('');
  const [transferPendingBanner, setTransferPendingBanner] = useState(null);
  const [showInventoryTicketModal, setShowInventoryTicketModal] = useState(false);
  const [inventoryTickets, setInventoryTickets] = useState([]);
  const [inventoryTicketsLoading, setInventoryTicketsLoading] = useState(true);
  const [inventoryTicketsExpanded, setInventoryTicketsExpanded] = useState(false);
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  const [outcomeModalDismissedThisMount, setOutcomeModalDismissedThisMount] = useState(false);
  const [acknowledgingOutcome, setAcknowledgingOutcome] = useState(false);

  const currentParticipant = !user ? null : user.role === 'site' ? { type: 'site', id: user.siteId } : { type: 'user', id: user.id };

  const completedSiteIds = useMemo(() => {
    const ids = [];
    const seen = new Set();
    for (const b of counts || []) {
      if (b.status === 'completed' && b.siteId && !seen.has(b.siteId)) {
        seen.add(b.siteId);
        ids.push(b.siteId);
      }
    }
    return ids;
  }, [counts]);

  const mySites = useMemo(() => {
    return (managerSites || []).map(s => {
      return {
        siteId: s.id,
        siteName: s.name || 'Unknown',
        siteEmail: (s.email != null && String(s.email).trim()) ? String(s.email).trim() : '',
        status: managerSiteSubmissionStatus(counts, s.id)
      };
    }).sort((a, b) => a.siteName.localeCompare(b.siteName));
  }, [managerSites, counts]);

  const mySitesNotSubmittedCount = useMemo(() => mySites.filter(s => s.status === 'Not Submitted').length, [mySites]);

  const toggleDark = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
  };

  useEffect(() => {
    localStorage.setItem('managerDashboard_activeTab', activeTab);
  }, [activeTab]);

  useEffect(() => { fetchData(); loadInventoryTickets(); }, []);

  const loadInventoryTickets = async () => {
    setInventoryTicketsLoading(true);
    try {
      const res = await axiosInstance.get('/api/tenant/manager/missing-item-tickets');
      setInventoryTickets(res.data.tickets || []);
    } catch (err) {
      console.error('Error fetching manager inventory tickets:', err);
    } finally {
      setInventoryTicketsLoading(false);
    }
  };

  const unseenOutcomeTickets = inventoryTickets.filter((t) => t.outcomeUnseen);

  useEffect(() => {
    if (unseenOutcomeTickets.length > 0 && !outcomeModalDismissedThisMount) {
      setShowOutcomeModal(true);
    }
  }, [inventoryTickets.length, unseenOutcomeTickets.length]);

  const handleAcknowledgeOutcome = async (ticketId) => {
    setAcknowledgingOutcome(true);
    try {
      await axiosInstance.put(`/api/tenant/manager/missing-item-tickets/${ticketId}/acknowledge-outcome`);
      await loadInventoryTickets();
    } catch (err) {
      console.error('Error acknowledging manager inventory outcome:', err);
    } finally {
      setAcknowledgingOutcome(false);
    }
  };

  const handleOpenOutcomeModal = () => {
    setOutcomeModalDismissedThisMount(false);
    setShowOutcomeModal(true);
  };

  const handleDismissOutcomeModal = () => {
    setOutcomeModalDismissedThisMount(true);
    setShowOutcomeModal(false);
  };

  const fetchData = async () => {
    setError('');
    setEmptyMessage('');
    try {
      const [countsRes, sitesRes, brandsRes, inventoryRes, limitRes] = await Promise.all([
        axiosInstance.get('/api/tenant/counts'),
        axiosInstance.get('/api/tenant/sites/manager-sites'),
        axiosInstance.get('/api/tenant/brands'),
        axiosInstance.get('/api/tenant/inventory'),
        axiosInstance.get('/api/tenant/settings/global-inventory-value-limit').catch(() => ({ data: { globalInventoryValueLimit: null, globalInventoryValueLimitsByCluster: {} } }))
      ]);
      const batches = countsRes.data.batches || countsRes.data.counts || [];
      setCounts(batches);
      setManagerSites(sitesRes.data.sites || []);
      setBrands(brandsRes.data.brands || []);
      setAllInventory(inventoryRes.data.items || []);
      setGlobalInventoryValueLimit(limitRes.data?.globalInventoryValueLimit ?? null);
      setGlobalInventoryValueLimitsByCluster(limitRes.data?.globalInventoryValueLimitsByCluster ?? {});
      if (batches.length === 0) {
        setEmptyMessage('You are all up to date, there are no counts ready for you to review.');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load counts');
    } finally {
      setLoading(false);
    }
    if (user?.role === 'manager') {
      try {
        const r = await axiosInstance.get('/api/tenant/stock-transfers/pending-actions');
        const d = r.data || {};
        setTransferPendingBanner(d.mustResolve ? { count: (d.pendingTransferIds || []).length } : null);
      } catch {
        setTransferPendingBanner(null);
      }
    }
  };

  const refreshTransferPending = async () => {
    if (user?.role !== 'manager') {
      setTransferPendingBanner(null);
      return;
    }
    try {
      const r = await axiosInstance.get('/api/tenant/stock-transfers/pending-actions');
      const d = r.data || {};
      setTransferPendingBanner(d.mustResolve ? { count: (d.pendingTransferIds || []).length } : null);
    } catch {
      setTransferPendingBanner(null);
    }
  };


  const handleSendReminders = async () => {
    setReminderSending(true);
    setReminderMessage('');
    try {
      const res = await axiosInstance.post('/api/tenant/manager/send-reminders');
      setManagerReminderCooldownUntil(res.data.reminderCooldownUntil);
      setReminderMessage('Reminder successfully sent to all sites in the list');
    } catch (e) {
      if (e.response?.status === 429) setManagerReminderCooldownUntil(e.response.data?.reminderCooldownUntil || null);
      else setReminderMessage(e.response?.data?.error || 'Failed to send reminders');
    } finally { setReminderSending(false); }
  };

  if (loading) return <div className="mgr-loading">Loading...</div>;

  const pendingBatches = counts.filter(c => c.status === 'pending').length;
  const completedBatches = counts.filter(c => c.status === 'completed').length;
  const totalSites = managerSites.length;
  const pendingSites = [...new Map(counts.filter(c => c.status === 'pending').map(c => [c.siteId, { siteId: c.siteId, siteName: c.siteName || 'Unknown', status: 'Pending Review' }])).values()].sort((a, b) => a.siteName.localeCompare(b.siteName));
  const completedSites = [...new Map(counts.filter(c => c.status === 'completed').map(c => [c.siteId, { siteId: c.siteId, siteName: c.siteName || 'Unknown', status: 'Completed' }])).values()].sort((a, b) => a.siteName.localeCompare(b.siteName));

  const tabs = [
    { key: 'count-review', label: 'Count Review', icon: ClipboardCheck },
    { key: 'transfers', label: 'Transfers', icon: ArrowLeftRight },
    { key: 'reports', label: 'Reports', icon: BarChart3 }
  ];

  return (
    <div className="app-layout">
      <Navbar user={user} onLogout={logout} managerNav={{ activeTab, setActiveTab }} onOpenSupport={() => setShowSupportModal(true)} />
      <div className="dashboard">
        <div className="mgr-top-bar">
          <div className="dashboard-header">
            <h2>Manager Dashboard</h2>
            <p>Review inventory counts</p>
          </div>
          <div className="mgr-top-bar-actions">
            <button
              type="button"
              className="mgr-theme-btn mgr-inventory-ticket-btn"
              onClick={() => setShowInventoryTicketModal(true)}
              title="Report Inventory Issue"
              aria-label="Report Inventory Issue"
            >
              <PackageMinus size={18} aria-hidden />
              <span className="mgr-inventory-ticket-btn-label">Report Inventory Issue</span>
            </button>
            <ChatBell
              onSelectConversation={(id, name) => {
                setChatConversationId(id);
                setChatOtherPartyDisplayName(name);
                setShowChatModal(true);
              }}
              buttonClassName="mgr-theme-btn"
              iconSize={18}
              refreshTrigger={chatRefreshTrigger}
              userRole={user?.role}
            />
            <button type="button" className="mgr-theme-btn" onClick={toggleDark} title={dark ? 'Light mode' : 'Dark mode'}>
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
        </div>

        {unseenOutcomeTickets.length > 0 && (
          <button type="button" className="sd-outcome-banner" onClick={handleOpenOutcomeModal}>
            <PackageMinus size={16} aria-hidden />
            The admin team has responded to {unseenOutcomeTickets.length === 1 ? 'an inventory ticket' : `${unseenOutcomeTickets.length} inventory tickets`}. Click to view.
          </button>
        )}

        <div className={'card sd-missing-tickets-section' + (inventoryTicketsExpanded ? ' sd-missing-tickets-expanded' : '')}>
          <button
            type="button"
            className="sd-missing-tickets-toggle"
            onClick={() => setInventoryTicketsExpanded((prev) => !prev)}
            aria-expanded={inventoryTicketsExpanded}
            aria-controls="mgr-inventory-tickets-panel"
          >
            <div className="sd-missing-tickets-toggle-main">
              <h3 className="sd-missing-tickets-title">My Inventory Tickets</h3>
              <p className="sd-missing-tickets-summary">
                Open: {inventoryTickets.filter((t) => t.status === 'open').length}
                {' · '}
                Closed: {inventoryTickets.filter((t) => t.status === 'closed').length}
              </p>
            </div>
            <ChevronDown
              size={18}
              className={'sd-missing-tickets-chevron' + (inventoryTicketsExpanded ? ' sd-missing-tickets-chevron-open' : '')}
              aria-hidden
            />
          </button>
          {inventoryTicketsExpanded && (
            <div id="mgr-inventory-tickets-panel" className="sd-missing-tickets-body">
              <MissingItemTicketsPanel
                tickets={inventoryTickets}
                mode="manager"
                loading={inventoryTicketsLoading}
                onRefresh={loadInventoryTickets}
                photoBasePath="/api/tenant/manager/missing-item-tickets"
              />
            </div>
          )}
        </div>

        {activeTab === 'count-review' && emptyMessage && (
          <div className="mgr-banner mgr-banner-success">
            {emptyMessage}
            <button type="button" className="btn btn-secondary mgr-banner-btn" onClick={fetchData}>Refresh</button>
          </div>
        )}
        {activeTab === 'count-review' && error && (
          <div className="mgr-banner mgr-banner-error">
            {error}
            <button type="button" className="btn btn-secondary mgr-banner-btn" onClick={fetchData}>Refresh</button>
          </div>
        )}
        {activeTab === 'count-review' && transferPendingBanner && (
          <div className="mgr-banner mgr-banner-warning">
            {transferPendingBanner.count} pending stock transfer request(s) involve site(s) you manage. Count review is blocked for those sites until each request is completed or rejected (sending site manager approves or rejects; recipient may cancel a pending request).{' '}
            <button type="button" className="btn btn-secondary mgr-banner-btn" onClick={() => setActiveTab('transfers')}>
              Open Transfers
            </button>
          </div>
        )}

        <div className="mgr-tabs">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button key={key} type="button" className={'mgr-tab' + (activeTab === key ? ' active' : '')} onClick={() => setActiveTab(key)}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>

        {activeTab === 'count-review' && (
          <>
            <div className="mgr-stats-row">
              <div className="card mgr-stat-card" role="button" tabIndex={0} onClick={() => { setSitesModalType('pending'); setShowSitesModal(true); }} onKeyDown={e => e.key === 'Enter' && (setSitesModalType('pending'), setShowSitesModal(true))}>
                <span className="mgr-stat-label">PENDING REVIEWS</span>
                <span className="mgr-stat-value mgr-stat-warning">{pendingBatches}</span>
              </div>
              <div className="card mgr-stat-card" role="button" tabIndex={0} onClick={() => { setSitesModalType('completed'); setShowSitesModal(true); }} onKeyDown={e => e.key === 'Enter' && (setSitesModalType('completed'), setShowSitesModal(true))}>
                <span className="mgr-stat-label">COMPLETED</span>
                <span className="mgr-stat-value mgr-stat-success">{completedBatches}</span>
              </div>
              <div className="card mgr-stat-card" role="button" tabIndex={0} onClick={() => { setSitesModalType('notSubmitted'); setReminderMessage(''); axiosInstance.get('/api/tenant/manager/reminder-cooldown').then(r => setManagerReminderCooldownUntil(r.data.reminderCooldownUntil || null)).catch(() => {}); setShowSitesModal(true); }} onKeyDown={e => e.key === 'Enter' && (setSitesModalType('notSubmitted'), setShowSitesModal(true))}>
                <span className="mgr-stat-label">MY SITES</span>
                <span className="mgr-stat-value">{totalSites}</span>
              </div>
            </div>
            <CountReview
              batches={counts}
              onRefresh={fetchData}
              globalInventoryValueLimit={globalInventoryValueLimit}
              globalInventoryValueLimitsByCluster={globalInventoryValueLimitsByCluster}
            />
          </>
        )}

        {activeTab === 'transfers' && (
          <ManagerTransfers
            managerSites={managerSites}
            allInventory={allInventory}
            brands={brands}
            user={user}
            completedSiteIds={completedSiteIds}
            onTransfersChanged={refreshTransferPending}
          />
        )}

        {activeTab === 'reports' && (
          <Reports
            managerMode={true}
            sites={managerSites}
            sitesOverride={managerSites}
            users={[]}
            brands={brands}
            inventory={allInventory}
          />
        )}

        {showChatModal && chatConversationId && (
          <ChatModal
            conversationId={chatConversationId}
            otherPartyDisplayName={chatOtherPartyDisplayName}
            onClose={() => {
              setShowChatModal(false);
              setChatConversationId(null);
              setChatOtherPartyDisplayName('');
              setChatRefreshTrigger(t => t + 1);
            }}
            currentParticipant={currentParticipant}
          />
        )}
        <ManagerSitesModal
          open={showSitesModal}
          type={sitesModalType}
          pendingSites={pendingSites}
          completedSites={completedSites}
          mySites={mySites}
          mySitesNotSubmittedCount={mySitesNotSubmittedCount}
          onClose={() => { setReminderMessage(''); setShowSitesModal(false); }}
          onSendReminder={handleSendReminders}
          reminderSending={reminderSending}
          reminderMessage={reminderMessage}
          reminderCooldownUntil={managerReminderCooldownUntil}
        />
        <SupportModal
          open={showSupportModal}
          onClose={() => setShowSupportModal(false)}
          tenantName={typeof window !== 'undefined' ? window.location.hostname : ''}
        />
        <MissingItemTicketModal
          open={showInventoryTicketModal}
          onClose={() => setShowInventoryTicketModal(false)}
          onSubmitted={loadInventoryTickets}
          mode="manager"
          submitUrl="/api/tenant/manager/missing-item-tickets"
          optionsUrl="/api/tenant/manager/missing-item-tickets/inventory-ticket-options"
          itemsUrl="/api/tenant/manager/missing-item-tickets/inventory-ticket-items"
        />
        {showOutcomeModal && unseenOutcomeTickets.length > 0 && (
          <div className="me-period-modal-overlay" onClick={handleDismissOutcomeModal} role="dialog" aria-modal="true" aria-labelledby="mgr-outcome-modal-title">
            <div className="card me-period-modal" onClick={(e) => e.stopPropagation()}>
              <h3 id="mgr-outcome-modal-title" className="me-period-modal-title">Inventory Ticket Update</h3>
              <p className="me-period-modal-desc">
                The admin team has responded to your inventory ticket:
              </p>
              <div className="sd-outcome-modal-ticket">
                <p className="sd-outcome-modal-item"><strong>Item:</strong> {unseenOutcomeTickets[0].itemDescription}</p>
                {unseenOutcomeTickets[0].ticketTypeLabel && (
                  <p className="sd-outcome-modal-item"><strong>Type:</strong> {unseenOutcomeTickets[0].ticketTypeLabel}</p>
                )}
                {unseenOutcomeTickets[0].requestedChange && (
                  <p className="sd-outcome-modal-item"><strong>Requested change:</strong> {unseenOutcomeTickets[0].requestedChange}</p>
                )}
                <p className="sd-outcome-modal-outcome"><strong>Outcome:</strong> {unseenOutcomeTickets[0].adminOutcome}</p>
              </div>
              {unseenOutcomeTickets.length > 1 && (
                <p className="sd-outcome-modal-more">
                  Plus {unseenOutcomeTickets.length - 1} more update{unseenOutcomeTickets.length - 1 === 1 ? '' : 's'} waiting after this one.
                </p>
              )}
              <div className="me-period-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={handleDismissOutcomeModal} disabled={acknowledgingOutcome}>Close</button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleAcknowledgeOutcome(unseenOutcomeTickets[0].id)}
                  disabled={acknowledgingOutcome}
                >
                  {acknowledgingOutcome ? 'Saving…' : 'Mark as seen'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagerDashboard;